import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plane, Car, Bus } from 'lucide-react';
import { StepIndicator, getSteps } from '@/components/booking/StepIndicator';
import { BookingConfirmation } from '@/components/booking/BookingConfirmation';
import { DurationStep } from '@/components/booking/DurationStep';
import { TripDetailsStep } from '@/components/booking/TripDetailsStep';
import { VehicleStep } from '@/components/booking/VehicleStep';
import { ConfirmStep } from '@/components/booking/ConfirmStep';
import { BookingHero, WizardNav, TrustBar } from '@/components/booking/BookingChrome';
import { TransferBooking } from '@/components/booking/TransferBooking';
import { BusBooking } from '@/components/booking/BusBooking';
import { ManagedContactStrip } from '@/components/FormEmbedSection';
import { TRANSFER_TERMS, BUS_TERMS } from '@/data/transportPricing';
import { useBookingWizard } from '@/hooks/useBookingWizard';
import { bt, getTerms } from '@/i18n/bookingI18n';

const STEP_COMPONENTS = [DurationStep, TripDetailsStep, VehicleStep, ConfirmStep];

const SERVICES = (lang) => [
  { id: 'transfer', title: bt(lang, 'svcTransferTitle'), desc: bt(lang, 'svcTransferDesc'), icon: Plane },
  { id: 'chauffeur', title: bt(lang, 'svcChauffeurTitle'), desc: bt(lang, 'svcChauffeurDesc'), icon: Car },
  { id: 'bus', title: bt(lang, 'svcBusTitle'), desc: bt(lang, 'svcBusDesc'), icon: Bus },
];

function ChauffeurWizard({ lang }) {
  const w = useBookingWizard(lang);
  const Step = STEP_COMPONENTS[w.step];
  if (w.submitted) return <BookingConfirmation form={w.form} bookingRef={w.bookingRef} getPrice={w.getPrice} onReset={w.reset} lang={lang} />;
  return (
    <div data-testid="booking-wizard" className="max-w-4xl mx-auto">
      <StepIndicator current={w.step} steps={getSteps(lang)} />
      <Step form={w.form} errors={w.errors} set={w.set} getPrice={w.getPrice} lang={lang} />
      {w.errors.submit && (
        <p data-testid="booking-submit-error" role="alert" className="mt-8 max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-600 font-body text-sm p-3 rounded-lg">{w.errors.submit}</p>
      )}
      <WizardNav step={w.step} submitting={w.submitting} onBack={w.back} onNext={w.next} onSubmit={w.submit} lang={lang} />
    </div>
  );
}

function BookingTerms({ service, lang }) {
  const terms = getTerms(lang, service, TRANSFER_TERMS, BUS_TERMS);
  const heading = service === 'bus' ? bt(lang, 'termsHeadingBus') : bt(lang, 'termsHeadingTransfer');
  return (
    <div data-testid="booking-terms" className="mt-14 sm:mt-20 max-w-4xl mx-auto">
      <ManagedContactStrip lang={lang} />
      <div className="mt-8 rounded-2xl border border-black/10 bg-white p-6 sm:p-8">
        <h3 className="font-heading font-bold text-xs text-[#EE5A01] uppercase tracking-[0.2em]">{heading}</h3>
        <ul className="mt-5 space-y-2.5">
          {terms.map((t) => (
            <li key={t} className="flex items-start gap-3 font-body text-sm text-[#555555] leading-relaxed"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#EE5A01] flex-shrink-0" />{t}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function BookChauffeurPage({ lang = 'en' }) {
  const [params, setParams] = useSearchParams();
  const requested = params.get('service');
  const services = SERVICES(lang);
  const service = services.some((s) => s.id === requested) ? requested : 'transfer';

  useEffect(() => { document.title = bt(lang, 'docTitle'); }, [lang]);

  const pick = (id) => setParams(id === 'transfer' ? {} : { service: id });

  return (
    <div data-testid="book-chauffeur-page" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <BookingHero lang={lang} />
      <section className="bg-[#FAFAFA] py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10 sm:mb-14">
            {services.map((s) => {
              const active = service === s.id;
              return (
                <button key={s.id} type="button" data-testid={`service-tab-${s.id}`} onClick={() => pick(s.id)}
                  className={`text-left rounded-2xl border p-5 transition-all ${active ? 'border-[#EE5A01] bg-[#FFF4ED]' : 'border-black/10 bg-white hover:border-[#EE5A01]/40'}`}>
                  <span className={`grid h-11 w-11 place-items-center rounded-lg ${active ? 'bg-[#EE5A01] text-white' : 'bg-[#F5F5F3] text-[#666666]'}`}><s.icon className="w-5 h-5" /></span>
                  <h3 className="mt-4 font-heading font-bold text-sm text-[#121212]">{s.title}</h3>
                  <p className="mt-1 font-body text-xs text-[#666666] leading-relaxed">{s.desc}</p>
                </button>
              );
            })}
          </div>

          {service === 'chauffeur' && <ChauffeurWizard lang={lang} />}
          {service === 'transfer' && <TransferBooking lang={lang} />}
          {service === 'bus' && <BusBooking lang={lang} />}
          <BookingTerms service={service} lang={lang} />
        </div>
      </section>
      <TrustBar lang={lang} />
    </div>
  );
}
